import React from 'react';
import PropTypes from 'prop-types';
import {
  Tabs as MuiTabs,
  Tab as MuiTab,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Styled Tabs component
const StyledTabs = styled(MuiTabs)(({ theme, variant = 'standard' }) => ({
  minHeight: 48,
  '& .MuiTabs-indicator': {
    height: variant === 'contained' ? 0 : 2,
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px 2px 0 0',
  },
  '& .MuiTabs-flexContainer': {
    gap: 1,
  },
  '& .MuiTabs-scrollButtons': {
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  },
}));

// Styled Tab component
const StyledTab = styled(MuiTab)(({ theme, variant = 'standard' }) => ({
  textTransform: 'none',
  minWidth: 0,
  minHeight: 48,
  padding: '12px 16px',
  borderRadius: '10px 10px 0 0',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(14),
    padding: '8px 12px',
  },
  '&.Mui-selected': {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.main,
    ...(variant === 'contained' && {
      backgroundColor: `${theme.palette.primary.main}10`,
    }),
  },
  '&.Mui-focusVisible': {
    backgroundColor: `${theme.palette.primary.main}20`,
  },
}));

// Tabs component
const Tabs = ({
  value,
  onChange,
  variant = 'standard',
  orientation = 'horizontal',
  children,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine if scrollable based on screen size
  const scrollable = isMobile || children.length > 4;

  return (
    <StyledTabs
      value={value}
      onChange={onChange}
      variant={scrollable ? 'scrollable' : 'standard'}
      scrollButtons={scrollable ? 'auto' : false}
      allowScrollButtonsMobile={scrollable}
      orientation={orientation}
      {...props}
    >
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          variant: variant,
        });
      })}
    </StyledTabs>
  );
};

// Tab component
const Tab = ({ label, ...props }) => {
  return <StyledTab label={label} disableRipple {...props} />;
};

Tabs.propTypes = {
  /**
   * The value of the currently selected tab.
   */
  value: PropTypes.any.isRequired,
  /**
   * Callback fired when the value changes.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['standard', 'contained']),
  /**
   * The orientation of the tabs.
   */
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
   * The content of the component (Tab components).
   */
  children: PropTypes.node,
};

Tab.propTypes = {
  /**
   * The label of the tab.
   */
  label: PropTypes.node,
  /**
   * The value of the tab.
   */
  value: PropTypes.any,
};

export { Tabs, Tab };
